/**
 * INT-0011 tier 2 — "Confirm your structure" (wizard step 3 of 4, carried
 * signups only). Shows the SAME structure the visitor previewed in the
 * staty.io AI teaser (carried through the sign-up URL) in the shared
 * StructureEditor, then materializes it via onboarding/build — the wizard
 * ends with the promise kept, not a buried pre-filled builder.
 *
 * Degrade contract (spec req 8/9): this step must NEVER hard-block the
 * wizard. No obtainable structure → silent skip to the design step, pending
 * keys left so the GetStarted pre-filled builder stays the backstop. A build
 * failure after the user reached this step → toast + continue to design.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/auth/clerkShim';
import CreateOnboardingShell from './CreateOnboardingShell';
import WizardStepPill from './WizardStepPill';
import StructureEditor, { cleanStructureDraft, StructureDraft } from '@components/StructureEditor';
import useOnboardingStore from '@store/onboardingStore';
import { browserTimeZone } from '@/utils/registrationDateTz';
import { trackEvent } from '@/utils/analytics';
import {
  readPendingStructure,
  readPendingDescription,
  clearPendingCarry,
  PendingStructure,
} from '@/components/CreateOrganization/PendingDescriptionBanner';

// INT-0050: silent skips land on the finish screen (the design step is gone).
const FINISH = '/create/done';
// INT-0043: after the structure exists, "Who's playing?" (step 4 of 5 on the
// carry flow) comes before the design step. The silent skips below (nothing
// to confirm, no org) still go straight to design: no divisions to fill.
const TEAMS_STEP = '/create/add-teams?step=4';

function toDraft(pending: PendingStructure): StructureDraft {
  return {
    name: pending.name || 'My Competition',
    contestType: pending.contestType,
    contests: pending.contests,
    divisions: pending.divisions.map((d) => ({
      name: d.name,
      format: d.format || 'round-robin',
      capacity: typeof d.capacity === 'number' ? d.capacity : 0,
    })),
  };
}

export default function CreateConfirmStructurePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken, orgId } = useAuth();

  const suggestStructure = useOnboardingStore((s: any) => s.suggestStructure(getToken));
  const buildStructure = useOnboardingStore((s: any) => s.buildStructure(getToken));

  const sport = searchParams.get('sport') || '';
  const contestTypeHint = searchParams.get('tournament') === '1' ? 'tournament' : 'season';

  const [structure, setStructure] = useState<StructureDraft | null>(() => {
    const pending = readPendingStructure();
    return pending ? toDraft(pending) : null;
  });
  const [generating, setGenerating] = useState(false);
  const [building, setBuilding] = useState(false);
  const bootstrapped = useRef(false);

  // No carried structure → regenerate from the description (the carry param
  // was corrupt/oversized). No description either, or no active org (direct
  // URL visit) → this step has nothing to confirm; silent skip.
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    if (structure) return;
    const desc = readPendingDescription();
    if (!desc || !orgId) {
      navigate(FINISH, { replace: true });
      return;
    }
    setGenerating(true);
    suggestStructure(orgId, desc, contestTypeHint)
      .then((result: any) => {
        setStructure({
          name: result?.name || 'My Competition',
          contestType: result?.contestType === 'tournament' ? 'tournament' : 'season',
          contests: Array.isArray(result?.contests) ? result.contests : [],
          divisions: Array.isArray(result?.divisions) && result.divisions.length
            ? result.divisions
            : [{ name: 'Open Division', format: 'round-robin', capacity: 8 }], // capacity 8: FORM SEED, not a platform default (ADR 0035)
        });
      })
      .catch(() => {
        // Silent degrade — keys stay for the GetStarted backstop.
        navigate(FINISH, { replace: true });
      })
      .finally(() => setGenerating(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async () => {
    if (!structure || building || !orgId) return;
    const clean = cleanStructureDraft(structure);
    if (!clean.divisions.length) {
      toast.error('Add at least one division.');
      return;
    }
    setBuilding(true);
    try {
      // ONE round-trip: structure + its open registration (ADR 0037/0038).
      const res = await buildStructure(orgId, {
        ...clean,
        registration: { open: true, timeZone: browserTimeZone() },
      });
      trackEvent('teaser_structure_built', {
        contest_type: res?.contestType || clean.contestType,
        sport,
      });
      clearPendingCarry(); // consumed — GetStarted must not re-prompt
      if (res?.registrationError) {
        toast.warning(`${clean.name} created, but its registration could not be created. Open one from Get Started.`);
      } else {
        toast.success(`${clean.name} created with ${clean.divisions.length} division${clean.divisions.length > 1 ? 's' : ''}. Registration is open.`);
      }
      navigate(TEAMS_STEP);
    } catch {
      // Never hard-block the wizard (spec req 9); keys stay for GetStarted.
      toast.error("We couldn't create your structure right now. You can build it from Get Started after setup.");
      navigate(TEAMS_STEP);
    } finally {
      setBuilding(false);
    }
  };

  return (
    <CreateOnboardingShell>
      <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pb-16 pt-4">
        <div className="w-full max-w-[640px]">
          <div className="mb-8 text-center">
            <WizardStepPill step={3} total={4} />
            <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Confirm your structure
            </h1>
            <p className="mx-auto mt-3 max-w-md text-base text-muted-foreground">
              {generating
                ? 'Drafting your structure from your description…'
                : "Here's what we drafted from your description. Tweak anything, then create it."}
            </p>
          </div>

          <div className="rounded-[28px] bg-card p-8 shadow-2xl ring-1 ring-border">
            {generating || !structure ? (
              <div className="flex items-center justify-center gap-3 py-12 text-muted-foreground">
                <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Building your draft…
              </div>
            ) : (
              <>
                <StructureEditor value={structure} onChange={setStructure} showTypeSelect />
                <button
                  type="button"
                  onClick={create}
                  disabled={building}
                  data-testid="confirm-structure-create"
                  className="mt-6 w-full rounded-2xl bg-gradient-to-r from-primary to-primary-deep px-6 py-4 text-base font-semibold text-white shadow-xl shadow-primary/25 transition-all duration-300 hover:from-primary/90 hover:to-primary-deep/90 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {building ? 'Creating your structure…' : 'Create it (registration opens too)'}
                </button>
              </>
            )}
          </div>

          <p className="mt-6 text-center text-sm text-muted-foreground/60">
            <button
              type="button"
              onClick={() => navigate(TEAMS_STEP)}
              className="font-medium text-muted-foreground underline-offset-2 hover:underline"
            >
              Skip for now
            </button>
            . You can build this anytime from Get Started.
          </p>
        </div>
      </main>
    </CreateOnboardingShell>
  );
}
